import React, {FC, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useFormikContext} from 'formik';
import {IOption, IWithDisabled} from 'models';
import {
  FormNameEnum,
  delayTime,
  fetchResourcesThunk,
  resourcesEntitiesCollectionSelector,
  loadingStateResources,
  dynamicSearchMessages,
} from 'filters-selections';
import {Form} from 'forms';
import {useAppDispatch, useDebounce} from 'hooks';
import {ItemWithPlaceholder} from '../ItemWithPlaceholder';
import {RESOURCE_FIELD_TOOLTIPS} from '../../../utils/tooltipConstants';

type AssociatedResourcesMultiSelectProps = IWithDisabled;

/** Initial options come from page-level prefetch; only refetch when the user types a search query. */
export const AssociatedResourcesMultiSelect: FC<AssociatedResourcesMultiSelectProps> = ({disabled}) => {
  const dispatch = useAppDispatch();
  const resources = useSelector(resourcesEntitiesCollectionSelector);
  const loading = useSelector(loadingStateResources);
  const {values} = useFormikContext<Record<string, unknown>>();

  const options: IOption[] = useMemo(() => {
    const selectedEntries = Array.isArray(values?.[FormNameEnum.associatedResources])
      ? (values[FormNameEnum.associatedResources] as unknown[])
      : [];

    const fromStore = (resources ?? []).map(({id, displayText, name}) => ({
      value: id,
      label: displayText || name || String(id),
    }));

    // Keep hydrated selections visible even when they are not in the current search page.
    const selectedOptions = selectedEntries
      .map((entry) => {
        if (entry !== null && typeof entry === 'object') {
          const obj = entry as {id?: string | number; value?: string | number; name?: string; label?: string};
          const id = obj.id ?? obj.value;
          if (id === undefined || id === null || id === '') {
            return null;
          }
          return {value: id, label: obj.name || obj.label || String(id)};
        }
        if (entry === null || entry === undefined || entry === '') {
          return null;
        }
        return {value: entry as string | number, label: String(entry)};
      })
      .filter((option): option is IOption => !!option)
      .filter((selected) => !fromStore.some((option) => String(option.value) === String(selected.value)));

    return [...selectedOptions, ...fromStore];
  }, [resources, values]);

  const debouncedSearch = useDebounce(
    (query: string) =>
      dispatch(
        fetchResourcesThunk({
          filter: {
            query,
          },
        })
      ),
    delayTime
  );

  const handleInputChange = useCallback(
    (value: string) => {
      const newValue = value.trim();
      return !!newValue && debouncedSearch(newValue);
    },
    [debouncedSearch]
  );

  return (
    <ItemWithPlaceholder label="Associated Resources" hasItems infoText={RESOURCE_FIELD_TOOLTIPS.associatedResources}>
      <Form.SearchSelectAutocomplete
        loading={loading}
        name={FormNameEnum.associatedResources}
        options={options}
        disabled={disabled}
        noOptionsText={dynamicSearchMessages.noOptionsResource}
        inputChangeHandler={handleInputChange}
        withFontWeightBold={true}
        withCheckboxRenderOption={false}
        multiple
      />
    </ItemWithPlaceholder>
  );
};
